import { LoggingFacade } from '@vwdfive/myvw-logging';
import { DevicePlatform } from '../enums';
import { TwoFactorManagementClient } from './TwoFactorManagementClient';
import { MissingScopeSpinError } from './errors/MissingScopeSpinError';
import { IWeConnectVehicle } from '../vcf';

export interface ISpinAvailability {
    readonly isAvailable: boolean;
    readonly spinChangeLockTime: number;
    readonly remainingTries: number;
    readonly hasMissingScopeError?: boolean;
}

export class SpinService {
    constructor(
        private readonly twoFactorManagementClient: TwoFactorManagementClient,
        private readonly loggingFacade: LoggingFacade,
    ) {
    }

    public async getSpinAvailability(enrolledVehicles: IWeConnectVehicle[]): Promise<ISpinAvailability> {
        try {
            const spinStateResponse = await this.twoFactorManagementClient.getSpinState(
                this.isFirstVehicleWCar(enrolledVehicles)
            );
            return {
                isAvailable: spinStateResponse.spinState === 'DEFINED' || spinStateResponse.spinState === 'LOCKED',
                spinChangeLockTime: spinStateResponse.spinLockedWaitingTime ?? 0,
                remainingTries: spinStateResponse.remainingTries ?? 0,
            };
        } catch (e: any) {
            this.loggingFacade.error('Could not get spin availability', e);
            const hasMissingScopeError = e instanceof MissingScopeSpinError;
            return { isAvailable: false, spinChangeLockTime: 0, remainingTries: 0, hasMissingScopeError };
        }
    }

    public async changeSpin(currentSpin: string, newSpin: string, enrolledVehicles: IWeConnectVehicle[]): Promise<void> {
        await this.twoFactorManagementClient.changeSpin(currentSpin, newSpin, this.isFirstVehicleWCar(enrolledVehicles));
    }

    public async resetSpin(newSpin: string, enrolledVehicles: IWeConnectVehicle[]): Promise<void> {
        await this.twoFactorManagementClient.resetSpin(newSpin, this.isFirstVehicleWCar(enrolledVehicles));
    }

    public async verifySpin(currentSpin: string, enrolledVehicles: IWeConnectVehicle[]): Promise<string> {
        return await this.twoFactorManagementClient.verifySpin(currentSpin, this.isFirstVehicleWCar(enrolledVehicles));
    }

    private isFirstVehicleWCar = (enrolledVehicles: IWeConnectVehicle[]): boolean =>
        enrolledVehicles[0]?.devicePlatform === DevicePlatform.WCAR;
}
